import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { CircularProgress, LinearScore } from "../components/Progress";
import { ArrowLeft, Sparkles, Heart, Handshake, MessageCircle, AlertTriangle, Copy, Check, Trash2, Share2, Download } from "lucide-react";

const FLAG_LABELS = {
  manipulation: "Manipulace",
  ghosting: "Ghosting",
  love_bombing: "Love bombing",
  narcissism: "Narcistické chování",
  gaslighting: "Gaslighting",
  lying: "Lhaní",
  breadcrumbing: "Breadcrumbing",
  stonewalling: "Stonewalling (mlčení jako trest)",
  other: "Toxický vzorec",
};

const SEVERITY = {
  high: { color: "bg-rose-100 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  medium: { color: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  low: { color: "bg-stone-100 text-stone-700 border-stone-200", dot: "bg-stone-400" },
};

export default function AnalysisResult() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/analyses/${id}`);
        setData(data);
      } catch { setError("Nepodařilo se načíst analýzu."); }
    })();
  }, [id]);

  const remove = async () => {
    if (!window.confirm("Opravdu chceš tuto analýzu smazat?")) return;
    await api.delete(`/analyses/${id}`);
    nav("/analyses");
  };

  if (error) return <div className="app-shell bg-aurora min-h-screen p-10 text-center text-rose-600">{error}</div>;
  if (!data) return <LoadingResult />;

  const r = data.result || {};
  const score = r.relationship_score || {};
  const flags = r.red_flags || [];
  const verdict = r.verdict || {};

  return (
    <div className="app-shell bg-aurora min-h-screen pb-24" data-testid="analysis-result-page">
      <div className="px-6 pt-10 pb-5 flex items-center justify-between">
        <button onClick={() => nav("/analyses")} className="text-[#5F7F77]" data-testid="result-back">
          <ArrowLeft size={20} strokeWidth={1.7} />
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowShare(true)} className="text-[#C9A66B] flex items-center gap-1 text-sm px-3 py-1.5 rounded-full bg-white border border-[#DEE7E2]" data-testid="result-share">
            <Share2 size={15} /> Sdílet
          </button>
          <button onClick={remove} className="text-[#5F7F77]" data-testid="result-delete">
            <Trash2 size={18} strokeWidth={1.7} />
          </button>
        </div>
      </div>

      <div className="px-6 space-y-5">
        {/* Header */}
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#5F7F77] font-medium">Analýza · {data.source}</p>
          <h1 className="font-display text-3xl text-[#1A2924] leading-tight mt-1">
            {data.their_name ? `Konverzace s ${data.their_name}` : "Analýza konverzace"}
          </h1>
          <p className="text-sm text-[#54635F] mt-1.5">{r.summary}</p>
        </div>

        {/* Verdict */}
        <div className="rounded-3xl p-6 bg-gradient-to-br from-[#082A24] to-[#0F3D34] text-white shadow-lg" data-testid="result-verdict">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">{verdict.icon?.split(" ")[0]}</span>
            <span className="text-xs uppercase tracking-widest text-white/70">Verdikt</span>
          </div>
          <h2 className="font-display text-2xl mb-2">{verdict.headline || "—"}</h2>
          <p className="text-sm text-white/85 leading-relaxed">{verdict.advice}</p>
        </div>

        {/* Relationship Score */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-[#5F7F77]">Relationship Score</p>
              <h3 className="font-display text-xl text-[#1A2924]">Celkové hodnocení</h3>
            </div>
            <CircularProgress value={score.overall || 0} size={80} stroke={7} />
          </div>
          <div className="space-y-3">
            <LinearScore value={score.interest || 0} label="❤️ Zájem" />
            <LinearScore value={score.respect || 0} label="🤝 Respekt" />
            <LinearScore value={score.communication || 0} label="💬 Komunikace" />
            <LinearScore value={score.consistency || 0} label="⚠️ Konzistence" />
          </div>
        </div>

        {/* What is happening */}
        <div className="rounded-3xl p-6 bg-gradient-to-br from-[#F0E8D6] via-[#EDE5D2] to-[#F5EFE0] border border-[#DEE7E2]" data-testid="result-explanation">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#082A24] to-[#0F3D34] flex items-center justify-center">
              <Sparkles size={14} className="text-white" />
            </div>
            <span className="font-display text-lg text-[#1A2924]">Co se v komunikaci děje</span>
          </div>
          <p className="text-[#1A2924] leading-relaxed text-sm">{r.what_is_happening}</p>
        </div>

        {/* Red flags */}
        {flags.length > 0 && (
          <div className="glass-card p-6" data-testid="result-redflags">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={18} className="text-rose-500" strokeWidth={1.8} />
              <h3 className="font-display text-xl text-[#1A2924]">Red flags ({flags.length})</h3>
            </div>
            <div className="space-y-3">
              {flags.map((f, i) => {
                const sev = SEVERITY[f.severity] || SEVERITY.medium;
                return (
                  <div key={`${f.type || "flag"}-${i}`} className="border border-[#DEE7E2] rounded-2xl p-4" data-testid={`flag-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex w-2 h-2 rounded-full ${sev.dot}`} />
                      <span className="font-semibold text-[#1A2924]">{f.label || FLAG_LABELS[f.type] || "Vzorec"}</span>
                      <span className={`ml-auto text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded-full border ${sev.color}`}>{f.severity}</span>
                    </div>
                    {f.evidence && (
                      <blockquote className="text-xs italic text-[#54635F] border-l-2 border-[#5F7F77] pl-3 my-2">
                        „{f.evidence}"
                      </blockquote>
                    )}
                    <p className="text-sm text-[#1A2924] leading-relaxed">{f.explanation}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Patterns */}
        {r.patterns?.length > 0 && (
          <div className="glass-card p-6">
            <h3 className="font-display text-xl text-[#1A2924] mb-3">🔁 Opakující se vzorce</h3>
            <ul className="space-y-2">
              {r.patterns.map((p, i) => (
                <li key={`p-${i}-${String(p).slice(0,20)}`} className="flex items-start gap-2 text-sm text-[#1A2924] leading-relaxed">
                  <span className="text-[#5F7F77] mt-0.5">●</span>{p}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Strengths */}
        {r.strengths?.length > 0 && (
          <div className="glass-card p-6">
            <h3 className="font-display text-xl text-[#1A2924] mb-3">✨ Silné stránky komunikace</h3>
            <ul className="space-y-2">
              {r.strengths.map((s, i) => (
                <li key={`s-${i}-${String(s).slice(0,20)}`} className="flex items-start gap-2 text-sm text-[#1A2924] leading-relaxed">
                  <span className="text-emerald-500 mt-0.5">●</span>{s}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Response suggestions */}
        {r.response_suggestions?.length > 0 && (
          <div className="rounded-3xl p-6 bg-gradient-to-br from-[#F0E8D6] to-[#EDE5D2] border border-[#DEE7E2]" data-testid="result-responses">
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle size={18} className="text-[#C9A66B]" strokeWidth={1.7} />
              <h3 className="font-display text-xl text-[#1A2924]">Návrhy odpovědi</h3>
            </div>
            <div className="space-y-3">
              {r.response_suggestions.map((s, i) => <SuggestionCard key={`sug-${i}-${String(s?.text || s).slice(0,20)}`} s={s} i={i} />)}
            </div>
          </div>
        )}

        <Link to="/analyze">
          <button className="btn-secondary" data-testid="another-analysis">
            ✨ Analyzovat další konverzaci
          </button>
        </Link>
      </div>

      {showShare && <ShareModal data={data} onClose={() => setShowShare(false)} />}
    </div>
  );
}

function ShareModal({ data, onClose }) {
  const r = data.result || {};
  const score = r.relationship_score || {};
  const verdict = r.verdict || {};
  const flagCount = (r.red_flags || []).length;
  const shareText = `Moje Relationship Score: ${score.overall || 0}% · ${verdict.headline || ""} · zjištěno v Sentře 💖`;

  const shareNative = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "Sentra — moje analýza", text: shareText, url: window.location.origin });
      } else {
        await navigator.clipboard.writeText(shareText + "\n" + window.location.origin);
        alert("Text zkopírován ✨");
      }
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm" data-testid="share-modal">
      <div className="bg-white w-full max-w-md rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="w-12 h-1 bg-[#FFE4CC] rounded-full mx-auto mb-4" />
        <h2 className="font-display text-2xl text-[#1A2924] mb-1">Sdílet anonymně</h2>
        <p className="text-sm text-[#54635F] mb-5">Žádná jména, žádné citace. Jen tvůj výsledek. ✨</p>

        {/* Share card preview */}
        <div className="rounded-3xl p-7 bg-gradient-to-br from-[#082A24] via-[#0F3D34] to-[#1A4D44] text-white shadow-2xl mb-5 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#C9A66B]/20 blur-2xl" />
          <div className="absolute -bottom-12 -left-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <p className="font-display text-sm tracking-[0.2em] uppercase mb-1 opacity-75">Sentra AI ❤️</p>
            <h3 className="font-display text-3xl mb-6 leading-tight">Moje Relationship Score</h3>
            <p className="font-display text-7xl mb-2">{score.overall || 0}<span className="text-3xl opacity-60">%</span></p>
            <p className="text-lg italic opacity-90 mb-6">{verdict.headline || ""}</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white/15 backdrop-blur rounded-2xl p-3"><span className="opacity-75 text-xs uppercase tracking-wider">Zájem</span><br /><strong className="text-xl font-display">{score.interest || 0}%</strong></div>
              <div className="bg-white/15 backdrop-blur rounded-2xl p-3"><span className="opacity-75 text-xs uppercase tracking-wider">Respekt</span><br /><strong className="text-xl font-display">{score.respect || 0}%</strong></div>
              <div className="bg-white/15 backdrop-blur rounded-2xl p-3"><span className="opacity-75 text-xs uppercase tracking-wider">Komunikace</span><br /><strong className="text-xl font-display">{score.communication || 0}%</strong></div>
              <div className="bg-white/15 backdrop-blur rounded-2xl p-3"><span className="opacity-75 text-xs uppercase tracking-wider">🚩 Red flags</span><br /><strong className="text-xl font-display">{flagCount}</strong></div>
            </div>
            <p className="text-center text-xs mt-6 opacity-70">sentra.app · AI, která ti pomůže pochopit vztahy</p>
          </div>
        </div>

        <button onClick={shareNative} className="btn-primary flex items-center justify-center gap-2" data-testid="share-native">
          <Share2 size={16} /> Sdílet text
        </button>
        <button onClick={onClose} className="block w-full text-center text-sm text-[#5F7F77] mt-3" data-testid="share-close">Zavřít</button>
      </div>
    </div>
  );
}

function SuggestionCard({ s, i }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(s.text); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="bg-white rounded-2xl p-4 border border-[#DEE7E2] shadow-sm" data-testid={`suggestion-${i}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[0.7rem] uppercase tracking-widest text-[#C9A66B] font-medium">{s.label || s.tone}</span>
        <button onClick={copy} className="text-[#C9A66B] p-1.5 hover:bg-[#FFE4CC] rounded-full" data-testid={`copy-suggestion-${i}`}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
      <p className="text-sm text-[#1A2924] leading-relaxed">{s.text}</p>
    </div>
  );
}

function LoadingResult() {
  return (
    <div className="app-shell bg-aurora min-h-screen p-6 pt-12 space-y-4">
      <div className="h-12 w-2/3 shimmer rounded-2xl" />
      <div className="h-32 shimmer rounded-3xl" />
      <div className="h-48 shimmer rounded-3xl" />
      <div className="h-40 shimmer rounded-3xl" />
    </div>
  );
}
